import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppBar, Toolbar, Theme, Box, IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { GVIconText } from 'components/icons';
import { inspection, app, files, getName, getInspectionId } from 'store';
import { UserInfo, ProjectName } from 'components';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import AddLinkIcon from '@mui/icons-material/AddLink';
import { useTracker } from '../Tracker/TrackerProvider';
import { isPrepUrl, isResultUrl, isReportsUrl } from 'utils/location';
import { useState } from 'react';
import InspectionCopyPrepModal from 'components/modals/InspectionCopyPrepModal';
import { isInternal } from 'components/Guards/InternalGuard';
import CTFVersionSelector from './CTFVersionSelector';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styles from './TopNav.module.css';

const useStyle = makeStyles()((theme: Theme) => ({
  icon: {
    height: '30px',
    width: 'auto',
  },
  copyPrepButton: {
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    color: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
      color: 'rgba(255, 255, 255, 1)',
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
    },
  },
}));

const TopNav = () => {
  const { classes } = useStyle();
  const dispatch = useDispatch();
  const tracker = useTracker();
  const location = useLocation();
  const inspectionName = useSelector(getName);
  const inspectionId = useSelector(getInspectionId);
  const [copyPrepModalOpen, setCopyPrepModalOpen] = useState<boolean>(false);
  const { showLinkInspections } = useFlags();

  const isInInspection = isPrepUrl(location) || isResultUrl(location) || isReportsUrl(location);

  const handleResetInspection = () => {
    tracker.track({
      name: 'Home-page',
    });
    dispatch(app.actions.resetStore());
    dispatch(files.actions.resetStore());
    dispatch(inspection.actions.resetStore());
    dispatch(app.actions.setShowLiveText(false));
  };

  const handleOpenCopyPrepModal = () => {
    setCopyPrepModalOpen(true);
  };

  const handleCloseCopyPrepModal = () => {
    setCopyPrepModalOpen(false);
  };

  return (
    <>
      <AppBar elevation={0} color="secondary">
        <Toolbar className={styles.root}>
          <GVTooltip title="Home" side="bottom">
            <Link data-testid="home_link" to="/" onClick={handleResetInspection}>
              <GVIconText className={classes.icon} />
            </Link>
          </GVTooltip>
          <Box display="flex" alignItems="center" gap="16px">
            <ProjectName />
          </Box>
          {showLinkInspections && isInInspection && inspectionId && inspectionName && (
            <GVTooltip title="Create Linked Inspection" side="bottom">
              <IconButton
                onClick={handleOpenCopyPrepModal}
                className={classes.copyPrepButton}
                id="open-linked-inspection-modal"
                size="small"
                aria-label="Create linked inspection"
              >
                <AddLinkIcon fontSize="small" />
              </IconButton>
            </GVTooltip>
          )}
          <Box display="flex" alignItems="center" marginLeft="auto">
            {isInternal && <CTFVersionSelector />}
            <UserInfo />
          </Box>
        </Toolbar>
      </AppBar>
      <Toolbar />

      {/* Create Linked Inspection Modal */}
      {showLinkInspections && isInInspection && inspectionId && inspectionName && (
        <InspectionCopyPrepModal
          open={copyPrepModalOpen}
          onClose={handleCloseCopyPrepModal}
          inspectionId={inspectionId}
          inspectionName={inspectionName}
        />
      )}
    </>
  );
};

export default TopNav;
